import { useEffect, useRef, useState } from 'react';
import type { MouseEvent, PointerEvent, RefObject } from 'react';
import type { WeeklySlot } from '@/lib/publishQueues/types';
import { calendarMinuteAt, calendarSelection } from '@/lib/publishQueues/calendarSelection';
import { editCalendarSlot, type CalendarEdit } from '@/lib/publishQueues/editCalendarSlot';

type Preview = { slot: WeeklySlot; index?: number };
type Gesture = {
  pointerId: number;
  capture: HTMLElement;
  column: HTMLElement;
  x: number;
  y: number;
  anchorMinute: number;
  dragged: boolean;
  original: WeeklySlot;
  index?: number;
  mode: CalendarEdit | 'create';
};

type Props = {
  scrollRef: RefObject<HTMLDivElement>;
  onAddSlot: (slot: WeeklySlot) => void;
  onUpdateSlot: (index: number, slot: WeeklySlot) => void;
};

export function useWeeklyScheduleDrag({ scrollRef, onAddSlot, onUpdateSlot }: Props) {
  const gesture = useRef<Gesture | null>(null);
  const pointer = useRef({ x: 0, y: 0 });
  const suppressClick = useRef(false);
  const [preview, setPreview] = useState<Preview | null>(null);

  const release = (active: Gesture) => {
    if (active.capture.hasPointerCapture?.(active.pointerId)) active.capture.releasePointerCapture(active.pointerId);
  };
  const cancel = () => {
    const active = gesture.current;
    gesture.current = null;
    setPreview(null);
    if (active) { suppressClick.current = true; release(active); }
  };
  const previewAt = (active: Gesture, x: number, y: number): Preview => {
    const bounds = active.column.getBoundingClientRect();
    if (active.mode === 'create') {
      return { slot: calendarSelection(active.original.weekday, active.anchorMinute, calendarMinuteAt(y, bounds.top, bounds.height)) };
    }
    let weekday = active.original.weekday;
    if (active.mode === 'move') {
      const columns = scrollRef.current?.querySelectorAll<HTMLElement>('[data-weekday]');
      let closest = Number.POSITIVE_INFINITY;
      columns?.forEach((column) => {
        const rect = column.getBoundingClientRect();
        const distance = Math.abs(x - (rect.left + rect.width / 2));
        if (distance < closest) { closest = distance; weekday = Number(column.dataset.weekday); }
      });
    }
    const minute = ((y - bounds.top) / bounds.height) * 1440;
    return { index: active.index, slot: editCalendarSlot(active.original, active.mode, minute - active.anchorMinute, weekday) };
  };
  // The animation loop reads current callbacks without restarting on every pointer preview.
  const previewAtRef = useRef(previewAt);
  previewAtRef.current = previewAt;
  const interacting = preview !== null;
  useEffect(() => {
    if (!interacting) return;
    const stop = () => {
      const active = gesture.current;
      gesture.current = null;
      suppressClick.current = true;
      setPreview(null);
      if (active) release(active);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Cancel before Radix's document handler can close the surrounding modal.
      event.preventDefault();
      event.stopPropagation();
      stop();
    };
    let frame: number;
    const scroll = () => {
      const active = gesture.current;
      const element = scrollRef.current;
      if (active?.dragged && element) {
        const rect = element.getBoundingClientRect();
        if (rect.height > 0) {
          const { x, y } = pointer.current;
          const top = element.scrollTop;
          const left = element.scrollLeft;
          if (y < rect.top + 60) element.scrollTop -= 6;
          else if (y > rect.bottom - 24) element.scrollTop += 6;
          if (active.mode === 'move') {
            if (x < rect.left + 68) element.scrollLeft -= 6;
            else if (x > rect.right - 24) element.scrollLeft += 6;
          }
          if (top !== element.scrollTop || left !== element.scrollLeft) setPreview(previewAtRef.current(active, x, y));
        }
      }
      frame = requestAnimationFrame(scroll);
    };
    frame = requestAnimationFrame(scroll);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('blur', stop);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('blur', stop);
    };
  }, [interacting, scrollRef]);

  const start = (event: PointerEvent<HTMLElement>, mode: Gesture['mode'], original: WeeklySlot, index?: number) => {
    event.stopPropagation();
    if (event.button !== 0 || event.isPrimary === false || gesture.current) return;
    const column = event.currentTarget.closest<HTMLElement>('[data-weekday]');
    if (!column) return;
    event.preventDefault();
    suppressClick.current = false;
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture(event.pointerId);
    const bounds = column.getBoundingClientRect();
    const anchorMinute = mode === 'create' ? calendarMinuteAt(event.clientY, bounds.top, bounds.height)
      : ((event.clientY - bounds.top) / bounds.height) * 1440;
    const active: Gesture = { pointerId: event.pointerId, capture: event.currentTarget, column,
      x: event.clientX, y: event.clientY, anchorMinute, dragged: false, original, index, mode };
    gesture.current = active;
    pointer.current = { x: event.clientX, y: event.clientY };
    setPreview({ index, slot: mode === 'create' ? calendarSelection(original.weekday, anchorMinute, anchorMinute) : original });
  };

  return {
    preview,
    startCreate: (event: PointerEvent<HTMLElement>, weekday: number) => start(event, 'create', { weekday, timeLocal: '00:00' }),
    startEdit: (event: PointerEvent<HTMLElement>, mode: CalendarEdit, index: number, slot: WeeklySlot) => start(event, mode, slot, index),
    consumeClick: (event: MouseEvent) => event.detail > 0 && suppressClick.current,
    handlers: {
      onPointerMove: (event: PointerEvent<HTMLElement>) => {
        const active = gesture.current;
        if (!active || active.pointerId !== event.pointerId) return;
        pointer.current = { x: event.clientX, y: event.clientY };
        active.dragged ||= Math.hypot(event.clientX - active.x, event.clientY - active.y) >= 3;
        if (active.dragged) setPreview(previewAt(active, event.clientX, event.clientY));
      },
      onPointerUp: (event: PointerEvent<HTMLElement>) => {
        const active = gesture.current;
        if (!active || active.pointerId !== event.pointerId) return;
        const next = previewAt(active, event.clientX, event.clientY);
        gesture.current = null;
        setPreview(null);
        suppressClick.current = active.dragged;
        release(active);
        if (active.mode === 'create') onAddSlot(next.slot);
        else if (active.dragged && active.index !== undefined) onUpdateSlot(active.index, next.slot);
      },
      onPointerCancel: cancel,
      onLostPointerCapture: cancel,
    },
  };
}
